import { useState } from 'react';
import {
    Box,
    Typography,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Grid,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import FeatherIcon from 'feather-icons-react';

const ApprovalStatuses = ({ soaId, soaData }) => {
    const [expanded, setExpanded] = useState(false);

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved':
                return 'success';
            case 'rejected':
                return 'error';
            case 'pending':
            default:
                return 'warning';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'approved':
                return 'check-circle';
            case 'rejected':
                return 'x-circle';
            case 'pending':
            default:
                return 'clock';
        }
    };

    const capitalizeFirstLetter = (string) => {
        if (!string) return 'Pending';
        return string.charAt(0).toUpperCase() + string.slice(1);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Pending';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const handleAccordionChange = () => {
        setExpanded(!expanded);
    };

    // 🔑 check if all team approvals are approved
    const allTeamApproved =
        soaData?.approvals?.length > 0 &&
        soaData.approvals.every((a) => a.status === 'approved');

    return (
        <Box className="soa-approval-statuses" sx={{ mt: 3 }}>
            <Accordion
                expanded={expanded}
                onChange={handleAccordionChange}
                sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    fontFamily: '"Times New Roman", Times, serif',
                }}
            >
                <AccordionSummary
                    expandIcon={<FeatherIcon icon={expanded ? 'chevron-up' : 'chevron-down'} size={16} />}
                    aria-controls="approval-statuses-content"
                    id="approval-statuses-header"
                    sx={{
                        bgcolor: 'grey.100',
                        '& .MuiAccordionSummary-content': {
                            alignItems: 'center',
                        },
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 'bold',
                            fontSize: '0.8rem',
                            color: '#1976d2',
                            textTransform: 'uppercase',
                            fontFamily: '"Times New Roman", Times, serif',
                        }}
                    >
                        <FeatherIcon icon="clipboard" size={16} style={{ marginRight: '8px' }} />
                        Approval Status
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2 }}>
                    {!soaData ? (
                        <Alert
                            severity="error"
                            sx={{ mx: 2, py: 1, fontFamily: '"Times New Roman", Times, serif' }}
                        >
                            <FeatherIcon icon="alert-triangle" size={16} style={{ marginRight: '8px' }} />
                            No approval data found.
                        </Alert>
                    ) : (
                        <>
                            {/* Team Approvals Section */}
                            {soaData.approvals?.length > 0 && (
                                <>
                                    <Typography
                                        variant="body1"
                                        className="soa-section-title"
                                        sx={{
                                            mb: 1,
                                            fontWeight: 'bold',
                                            color: '#1976d2',
                                            fontFamily: '"Times New Roman", Times, serif',
                                        }}
                                    >
                                        <FeatherIcon icon="users" size={16} style={{ marginRight: '8px' }} />
                                        Team Approvals ({soaData.approvals.length})
                                    </Typography>
                                    <Table size="small" className="soa-deductions-table">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                                                <TableCell sx={{ width: '20%', fontWeight: 'bold', fontFamily: '"Times New Roman", Times, serif' }}>
                                                    Approver
                                                </TableCell>
                                                <TableCell sx={{ width: '15%', fontWeight: 'bold', fontFamily: '"Times New Roman", Times, serif' }}>
                                                    Status
                                                </TableCell>
                                                <TableCell sx={{ width: '15%', fontWeight: 'bold', fontFamily: '"Times New Roman", Times, serif' }}>
                                                    Signed
                                                </TableCell>
                                                <TableCell sx={{ width: '50%', fontWeight: 'bold', fontFamily: '"Times New Roman", Times, serif' }}>
                                                    Comments
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {soaData.approvals.map((approval, index) => (
                                                <TableRow
                                                    key={index}
                                                    sx={{
                                                        '&:hover': { bgcolor: 'action.hover' },
                                                        transition: 'background-color 0.2s ease-in-out',
                                                    }}
                                                >
                                                    <TableCell sx={{ width: '20%', fontFamily: '"Times New Roman", Times, serif' }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#333' }}>
                                                            {approval.email}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatDate(approval.updated_at)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ width: '15%', fontFamily: '"Times New Roman", Times, serif' }}>
                                                        <Chip
                                                            icon={<FeatherIcon icon={getStatusIcon(approval.status)} size={14} />}
                                                            label={capitalizeFirstLetter(approval.status)}
                                                            color={getStatusColor(approval.status)}
                                                            size="small"
                                                            sx={{ fontFamily: '"Times New Roman", Times, serif', fontWeight: 500 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ width: '15%', fontFamily: '"Times New Roman", Times, serif' }}>
                                                        <Box
                                                            sx={{
                                                                width: 16,
                                                                height: 16,
                                                                borderRadius: '50%',
                                                                bgcolor: approval.signature && approval.signature.length > 10 ? '#28a745' : '#dc3545',
                                                                display: 'inline-block',
                                                                mr: 1,
                                                            }}
                                                            title={approval.signature && approval.signature.length > 10 ? 'Signed' : 'Not signed'}
                                                        />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {approval.signature && approval.signature.length > 10 ? 'Yes' : 'No'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ width: '50%', fontFamily: '"Times New Roman", Times, serif' }}>
                                                        {approval.comments ? (
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    whiteSpace: 'normal',
                                                                    wordBreak: 'break-word',
                                                                    color: '#333',
                                                                }}
                                                            >
                                                                {approval.comments}
                                                            </Typography>
                                                        ) : (
                                                            <Typography variant="caption" color="text.secondary">
                                                                No comments
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </>
                            )}

                            {/* 🔑 Auditor Approval Section (only if all team approved) */}
                            {allTeamApproved && (
                                <>
                                    <Typography
                                        variant="body1"
                                        className="soa-section-title"
                                        sx={{
                                            mt: 3,
                                            mb: 1,
                                            fontWeight: 'bold',
                                            color: '#1976d2',
                                            fontFamily: '"Times New Roman", Times, serif',
                                        }}
                                    >
                                        <FeatherIcon icon="user-check" size={16} style={{ marginRight: '8px' }} />
                                        Auditor Review
                                    </Typography>
                                    <Table size="small" className="soa-deductions-table">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                                                <TableCell sx={{ width: '20%', fontWeight: 'bold', fontFamily: '"Times New Roman", Times, serif' }}>
                                                    Auditor
                                                </TableCell>
                                                <TableCell sx={{ width: '15%', fontWeight: 'bold', fontFamily: '"Times New Roman", Times, serif' }}>
                                                    Status
                                                </TableCell>
                                                <TableCell sx={{ width: '15%', fontWeight: 'bold', fontFamily: '"Times New Roman", Times, serif' }}>
                                                    Signed
                                                </TableCell>
                                                <TableCell sx={{ width: '50%', fontWeight: 'bold', fontFamily: '"Times New Roman", Times, serif' }}>
                                                    Comments
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow
                                                sx={{
                                                    '&:hover': { bgcolor: 'action.hover' },
                                                    transition: 'background-color 0.2s ease-in-out',
                                                }}
                                            >
                                                <TableCell sx={{ width: '20%', fontFamily: '"Times New Roman", Times, serif' }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#333' }}>
                                                        {soaData.auditor_approval?.email || 'External Auditor'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatDate(soaData.auditor_approval?.updated_at)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ width: '15%', fontFamily: '"Times New Roman", Times, serif' }}>
                                                    <Chip
                                                        icon={<FeatherIcon icon={getStatusIcon(soaData.auditor_approval?.status)} size={14} />}
                                                        label={capitalizeFirstLetter(soaData.auditor_approval?.status || 'pending')}
                                                        color={getStatusColor(soaData.auditor_approval?.status)}
                                                        size="small"
                                                        sx={{ fontFamily: '"Times New Roman", Times, serif', fontWeight: 500 }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ width: '15%', fontFamily: '"Times New Roman", Times, serif' }}>
                                                    <Box
                                                        sx={{
                                                            width: 16,
                                                            height: 16,
                                                            borderRadius: '50%',
                                                            bgcolor: soaData.auditor_approval?.signature && soaData.auditor_approval.signature.length > 10 ? '#28a745' : '#dc3545',
                                                            display: 'inline-block',
                                                            mr: 1,
                                                        }}
                                                        title={soaData.auditor_approval?.signature && soaData.auditor_approval.signature.length > 10 ? 'Signed' : 'Not signed'}
                                                    />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {soaData.auditor_approval?.signature && soaData.auditor_approval.signature.length > 10 ? 'Yes' : 'No'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ width: '50%', fontFamily: '"Times New Roman", Times, serif' }}>
                                                    {soaData.auditor_approval?.comments ? (
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                whiteSpace: 'normal',
                                                                wordBreak: 'break-word',
                                                                color: '#333',
                                                            }}
                                                        >
                                                            {soaData.auditor_approval.comments}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="caption" color="text.secondary">
                                                            No comments
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </>
                            )}

                            {/* 🔑 Summary Stats */}
                            <Grid container spacing={2} sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                                <Grid item xs={4} sx={{ textAlign: 'center' }}>
                                    <Box sx={{ color: '#2e7d32' }}>
                                        <FeatherIcon icon="check-circle" size={16} />
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontFamily: '"Times New Roman", Times, serif' }}>
                                            {soaData.approvals?.filter((a) => a.status === 'approved').length || 0}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: '"Times New Roman", Times, serif' }}>
                                            Approved
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={4} sx={{ textAlign: 'center' }}>
                                    <Box sx={{ color: '#f57c00' }}>
                                        <FeatherIcon icon="clock" size={16} />
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontFamily: '"Times New Roman", Times, serif' }}>
                                            {(soaData.approvals?.filter((a) => a.status === 'pending').length || 0) +
                                                (allTeamApproved && soaData.auditor_approval?.status === 'pending' ? 1 : 0)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: '"Times New Roman", Times, serif' }}>
                                            Pending
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={4} sx={{ textAlign: 'center' }}>
                                    <Box sx={{ color: '#d32f2f' }}>
                                        <FeatherIcon icon="x-circle" size={16} />
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontFamily: '"Times New Roman", Times, serif' }}>
                                            {(soaData.approvals?.filter((a) => a.status === 'rejected').length || 0) +
                                                (allTeamApproved && soaData.auditor_approval?.status === 'rejected' ? 1 : 0)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: '"Times New Roman", Times, serif' }}>
                                            Rejected
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export { ApprovalStatuses };